import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { MY_SUBJECTS, ADD_SUBJECT, UPDATE_ATTENDANCE, DELETE_SUBJECT, MY_TIMETABLE } from '../graphql';
import { Plus, Trash2, ChevronUp, ChevronDown, AlertTriangle, CheckCircle, Link2, Calculator, Calendar } from 'lucide-react';

const TODAY_DAY = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][new Date().getDay()];

function computeStats(attended, total) {
  const pct = total === 0 ? 0 : (attended * 100) / total;
  const req75 = pct >= 75 ? 0 : Math.max(0, Math.ceil((0.75 * total - attended) / 0.25));
  const canBunk = pct <= 75 ? 0 : Math.max(0, Math.floor(attended / 0.75 - total));
  return { pct, req75, canBunk };
}

export default function AttendancePage() {
  const { data, loading } = useQuery(MY_SUBJECTS);
  const { data: ttData } = useQuery(MY_TIMETABLE);
  const [addSubject] = useMutation(ADD_SUBJECT, { refetchQueries: [MY_SUBJECTS] });
  const [updateAttendance] = useMutation(UPDATE_ATTENDANCE, { refetchQueries: [MY_SUBJECTS] });
  const [deleteSubject] = useMutation(DELETE_SUBJECT, { refetchQueries: [MY_SUBJECTS] });

  const [form, setForm] = useState({ name: '', totalClasses: '', attendedClasses: '' });
  const [showForm, setShowForm] = useState(false);

  // Miss simulator state: subjectId → missCount
  const [missCount, setMissCount] = useState({});

  // Timetable link: show today's classes as checkboxes
  const [showTimetable, setShowTimetable] = useState(false);
  const [markedSlots, setMarkedSlots] = useState({}); // slotId → 'present' | 'absent' | null

  const subjects = data?.mySubjects || [];
  const todaySlots = useMemo(() => {
    return (ttData?.myTimetable?.slots || [])
      .filter(s => s.day === TODAY_DAY && (s.type === 'lecture' || s.type === 'lab'))
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [ttData]);

  const overall = subjects.length
    ? (subjects.reduce((s, sub) => s + sub.percentage, 0) / subjects.length).toFixed(1)
    : 0;

  const handleAdd = async (e) => {
    e.preventDefault();
    await addSubject({ variables: { name: form.name, totalClasses: parseInt(form.totalClasses), attendedClasses: parseInt(form.attendedClasses) }});
    setForm({ name: '', totalClasses: '', attendedClasses: '' });
    setShowForm(false);
  };

  const markClass = async (sub, attended) => {
    await updateAttendance({ variables: { subjectId: sub.id, totalClasses: sub.totalClasses + 1, attendedClasses: sub.attendedClasses + (attended ? 1 : 0) }});
  };

  // Submit today's timetable attendance
  const submitTimetableAttendance = async () => {
    for (const [slotId, status] of Object.entries(markedSlots)) {
      if (!status) continue;
      const slot = todaySlots.find(s => s.id === slotId);
      if (!slot) continue;
      // Match slot title to subject (fuzzy)
      const sub = subjects.find(s =>
        s.name.toLowerCase().includes(slot.title.toLowerCase()) ||
        slot.title.toLowerCase().includes(s.name.toLowerCase())
      );
      if (!sub) continue;
      await updateAttendance({ variables: {
        subjectId: sub.id,
        totalClasses: sub.totalClasses + 1,
        attendedClasses: sub.attendedClasses + (status === 'present' ? 1 : 0)
      }});
    }
    setMarkedSlots({});
    setShowTimetable(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-brand-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attendance</h1>
          {subjects.length > 0 && <p className="text-gray-500 text-sm mt-1">Overall: <strong>{overall}%</strong></p>}
        </div>
        <div className="flex gap-2">
          {todaySlots.length > 0 && (
            <button className="btn-secondary flex items-center gap-2" onClick={() => setShowTimetable(!showTimetable)}>
              <Calendar size={16} /> Today's Classes
            </button>
          )}
          <button className="btn-primary flex items-center gap-2" onClick={() => setShowForm(!showForm)}>
            <Plus size={18} /> Add Subject
          </button>
        </div>
      </div>

      {/* Timetable link panel */}
      {showTimetable && (
        <div className="card mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Link2 size={18} className="text-brand-500" />
            <h3 className="font-semibold">Mark Today's Classes ({TODAY_DAY})</h3>
          </div>
          {todaySlots.length === 0 ? (
            <p className="text-sm text-gray-400">No classes scheduled today in your timetable.</p>
          ) : (
            <div className="space-y-3">
              {todaySlots.map(slot => {
                const matchedSub = subjects.find(s =>
                  s.name.toLowerCase().includes(slot.title.toLowerCase()) ||
                  slot.title.toLowerCase().includes(s.name.toLowerCase())
                );
                return (
                  <div key={slot.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div>
                      <p className="font-medium text-sm text-gray-900">{slot.title}</p>
                      <p className="text-xs text-gray-400">{slot.startTime}–{slot.endTime} · {slot.type}
                        {matchedSub ? ` · ${matchedSub.percentage.toFixed(1)}% now` : ' · subject not linked'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setMarkedSlots(m => ({ ...m, [slot.id]: m[slot.id] === 'present' ? null : 'present' }))}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${markedSlots[slot.id] === 'present' ? 'bg-green-500 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}>
                        Present
                      </button>
                      <button
                        onClick={() => setMarkedSlots(m => ({ ...m, [slot.id]: m[slot.id] === 'absent' ? null : 'absent' }))}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${markedSlots[slot.id] === 'absent' ? 'bg-red-500 text-white' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}>
                        Absent
                      </button>
                    </div>
                  </div>
                );
              })}
              <div className="flex gap-3 pt-2">
                <button className="btn-primary" onClick={submitTimetableAttendance}
                  disabled={Object.values(markedSlots).every(v => !v)}>
                  Save Attendance
                </button>
                <button className="btn-secondary" onClick={() => { setShowTimetable(false); setMarkedSlots({}); }}>Cancel</button>
              </div>
              {subjects.some(s => !todaySlots.some(slot =>
                s.name.toLowerCase().includes(slot.title.toLowerCase()) ||
                slot.title.toLowerCase().includes(s.name.toLowerCase())
              )) && (
                <p className="text-xs text-amber-600 mt-2">Tip: Name your timetable slots to match subject names for automatic linking</p>
              )}
            </div>
          )}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleAdd} className="card mb-6 space-y-4">
          <h3 className="font-semibold">Add Subject</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject Name</label>
              <input className="input" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Classes</label>
              <input className="input" type="number" min="0" required value={form.totalClasses} onChange={e => setForm({...form, totalClasses: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Attended</label>
              <input className="input" type="number" min="0" required value={form.attendedClasses} onChange={e => setForm({...form, attendedClasses: e.target.value})} />
            </div>
          </div>
          <div className="flex gap-3">
            <button className="btn-primary" type="submit">Save</button>
            <button className="btn-secondary" type="button" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      <div className="space-y-4">
        {subjects.map(sub => {
          const pct = sub.percentage;
          const safe = pct >= 75;
          const misses = missCount[sub.id] || 0;
          const sim = computeStats(sub.attendedClasses, sub.totalClasses + misses);

          return (
            <div key={sub.id} className="card">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{sub.name}</h3>
                  <p className="text-sm text-gray-500">{sub.attendedClasses}/{sub.totalClasses} classes</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`badge ${safe ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {pct.toFixed(1)}%
                  </span>
                  <button onClick={() => deleteSubject({ variables: { subjectId: sub.id }})}
                    className="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Progress bar */}
              <div className="h-2 bg-gray-100 rounded-full mb-3">
                <div className={`h-2 rounded-full transition-all ${safe ? 'bg-green-500' : 'bg-red-500'}`}
                  style={{ width: `${Math.min(100, pct)}%` }} />
              </div>

              {/* Info row */}
              <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                <div className="flex items-center gap-1">
                  {safe ? (
                    <><CheckCircle size={14} className="text-green-500" /> Can bunk <strong className="text-green-600">{sub.canBunk}</strong> more</>
                  ) : (
                    <><AlertTriangle size={14} className="text-red-500" /> Need <strong className="text-red-600">{sub.requiredToReach75}</strong> more to reach 75%</>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => markClass(sub, true)}
                    className="flex items-center gap-1 px-2 py-1 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition-colors">
                    <ChevronUp size={14} /> Present
                  </button>
                  <button onClick={() => markClass(sub, false)}
                    className="flex items-center gap-1 px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg transition-colors">
                    <ChevronDown size={14} /> Absent
                  </button>
                </div>
              </div>

              {/* Miss simulator */}
              <div className="border-t border-gray-50 pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <Calculator size={13} className="text-gray-400" />
                  <p className="text-xs font-medium text-gray-500">Miss Simulator</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500">If you miss</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setMissCount(m => ({ ...m, [sub.id]: Math.max(0, (m[sub.id]||0) - 1) }))}
                      className="w-6 h-6 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center text-sm font-bold">−</button>
                    <span className="w-8 text-center text-sm font-semibold text-gray-800">{misses}</span>
                    <button onClick={() => setMissCount(m => ({ ...m, [sub.id]: (m[sub.id]||0) + 1 }))}
                      className="w-6 h-6 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center text-sm font-bold">+</button>
                  </div>
                  <span className="text-xs text-gray-500">more classes →</span>
                  <span className={`font-bold text-sm ${sim.pct < 75 ? 'text-red-600' : sim.pct < 85 ? 'text-yellow-600' : 'text-green-600'}`}>
                    {sim.pct.toFixed(1)}%
                  </span>
                  {misses > 0 && (
                    <span className="text-xs text-gray-400">
                      ({sim.pct < 75 ? `need ${sim.req75} more to recover` : `can still bunk ${sim.canBunk}`})
                    </span>
                  )}
                </div>
                {misses > 0 && sim.pct < 75 && sub.percentage >= 75 && (
                  <p className="text-xs text-red-600 mt-1">Missing {misses} more classes will drop you below 75%</p>
                )}
              </div>
            </div>
          );
        })}

        {subjects.length === 0 && !showForm && (
          <div className="text-center py-16 text-gray-400">
            <CheckCircle size={48} className="mx-auto mb-3 opacity-30" />
            <p>No subjects yet. Add a subject to begin tracking.</p>
          </div>
        )}
      </div>
    </div>
  );
}
